import { ResponsiveSection, Stack } from '@stitch-fix/mode-react';
import type { GetStaticProps, NextPage } from 'next';
import {
  ContentGallery,
  type ContentGalleryProps,
} from '../../components/ContentGallery';
import { GetStartedBox } from '../../components/GetStartedBox';
import { Layout } from '../../components/Layout';
import { SEOData, type SEODataProps } from '../../components/SEOData';
import {
  TrigallHero,
  type TrigallHeroProps,
} from '../../components/TrigallHero';
import {
  TripleBlock,
  type TripleBlockProps,
} from '../../components/TripleBlock';
import {
  WhatClothesCost,
  type WhatClothesCostProps,
} from '../../components/WhatClothesCost/WhatClothesCost';
import {
  ImageTestimonials,
  type ImageTestimonialsProps,
} from '../../components/ImageTestimonials/ImageTestimonials';
import { getCachedEntry } from '../../utils/requests/contentstack/cachedRequests';
import { pricingSchema } from './schema';
import { serverLogger } from '../../utils/loggers/server';

export type PricingPageProps = {
  seoData: SEODataProps;
  hero: TrigallHeroProps;
  howItWorks: TripleBlockProps;
  aboutPricing: ContentGalleryProps;
  whatClothesCost: WhatClothesCostProps;
  testimonials: ImageTestimonialsProps;
};

export const getStaticProps: GetStaticProps<PricingPageProps> = async () => {
  try {
    const entry = await getCachedEntry({
      contentType: 'pricing',
      schema: pricingSchema,
      language: 'en-US',
      logger: serverLogger,
    });

    return {
      props: entry.data,
      // revalidate configures the cache policy, this sets it to cache for 5 minutes
      // https://nextjs.org/docs/pages/building-your-application/deploying/production-checklist#caching
      revalidate: 300,
    };
  } catch (error: unknown) {
    serverLogger({
      level: 'error',
      team: 'client-onboarding',
      message: 'Required page data is undefined - /pricing',
      context: {
        error,
        page: '/pricing',
        severity: 'critical',
      },
    });

    throw new Error('Required data not provided to render /pricing');
  }
};

const OurBrandsPage: NextPage<PricingPageProps> = props => {
  return (
    <Layout>
      <ResponsiveSection
        as="main"
        // setting this id allows the "Skip to page content" link to render from knit
        id="knit-main-content"
        mt="0"
        width={{ sm: 'full-bleed' }}
        gutter="none"
      >
        <SEOData {...props.seoData} />
        <Stack
          mb={{ sm: '3rem', lg: '4rem' }}
          spacing={{ sm: '3rem', lg: '4rem' }}
        >
          <TrigallHero {...props.hero} />
          <TripleBlock {...props.howItWorks} />
          <ContentGallery {...props.aboutPricing} />
          <WhatClothesCost {...props.whatClothesCost} />
          <ImageTestimonials {...props.testimonials} />
          <GetStartedBox />
        </Stack>
      </ResponsiveSection>
    </Layout>
  );
};

export default OurBrandsPage;
